import { useState } from "react";
import { Link } from "react-router-dom";
import { LogoMark } from "../lib/brand";
import { motion } from "framer-motion";
import { useLang, LangToggle } from "../lib/i18n";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Users, Wallet, HandCoins, Vote, Bot, Building2, ShieldCheck,
  FileCheck2, ScrollText, LayoutDashboard, MessagesSquare, Check, X,
  ArrowRight, Menu, Database, GitBranch,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1596422846543-75c6fc197f07?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY6NzN8MHwxfHNlYXJjaHwxfHxtYWxheXNpYSUyMG1vZGVybiUyMGJ1c2luZXNzJTIwZmluYW5jZSUyMHRlYW0lMjBjb29wZXJhdGl2ZXxlbnwwfHx8fDE3ODkyOTQyNDF8MA&ixlib=rb-4.1.0&q=85";
const BOARD_IMG = "https://images.unsplash.com/photo-1628655501446-fbfe7f92a7c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxhc2lhbiUyMGJ1c2luZXNzJTIwbWVldGluZyUyMGV4ZWN1dGl2ZSUyMGxhcHRvcCUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzg5Mjk0MjQ1fDA&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.08 } }),
};

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`py-20 sm:py-28 ${className}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
  </section>
);

const Eyebrow = ({ children, dark = false }) => (
  <div className={`text-xs font-semibold uppercase tracking-[0.2em] mb-3 ${dark ? "text-sky-400" : "text-sky-600"}`}>{children}</div>
);

const PLAN_CARDS = [
  { code: "starter", monthly: 299, testid: "plan-card-starter" },
  { code: "professional", monthly: 799, featured: true, testid: "plan-card-professional" },
  { code: "business", monthly: 2000, testid: "plan-card-business" },
];

const PLAN_FEATURES = {
  starter: ["Ahli & KYC", "Saham & Yuran", "Bayaran & Resit", "Portal Ahli", "Laporan & Dashboard Asas"],
  professional: ["Semua Starter", "Pembiayaan & Tunggakan", "Perakaunan & Bank", "AGM & E-Voting", "WhatsApp + Pembantu AI"],
  business: ["Semua Professional", "Perolehan & Vendor", "Belanjawan", "Tadbir Urus, Risiko & Audit", "AI Lanjutan"],
};

const FAQS = [
  ["Adakah data koperasi saya selamat?", "Ya. Setiap koperasi beroperasi dalam penyewa (tenant) terasing — data Koperasi A tidak boleh diakses oleh Koperasi B, dikuatkuasakan di peringkat pangkalan data.", "Is my cooperative's data safe?", "Yes. Each cooperative runs in an isolated tenant — Cooperative A's data can never be accessed by Cooperative B, enforced at the database level."],
  ["Bolehkah kami gunakan jenama sendiri?", "Ya. White label penuh: logo, nama jenama, warna, mesej log masuk dan domain tersuai.", "Can we use our own branding?", "Yes. Full white label: logo, brand name, colors, login message and custom domain."],
  ["Bagaimana dengan migrasi data lama?", "Kami menawarkan perkhidmatan migrasi berbayar daripada Excel, CSV atau sistem legasi dengan proses audit, pemetaan dan rekonsiliasi.", "What about migrating old data?", "We offer paid migration services from Excel, CSV or legacy systems with audit, mapping and reconciliation."],
  ["Adakah AI mematuhi kebenaran koperasi kami?", "Ya. Koperasi AI mematuhi kebenaran setiap penyewa dan hanya mengakses data koperasi anda sahaja.", "Does the AI respect our permissions?", "Yes. Koperasi AI obeys each tenant's permissions and only accesses your cooperative's data."],
];

export default function Landing() {
  const { t, lang } = useLang();
  const [menuOpen, setMenuOpen] = useState(false);
  const [cycle, setCycle] = useState("monthly");
  const [form, setForm] = useState({ coop_name: "", contact_person: "", email: "", phone: "", member_size: "", message: "" });
  const [demoSent, setDemoSent] = useState(false);

  const submitDemo = async (e) => {
    e.preventDefault();
    try {
      await api.post("/public/demo-request", form);
      setDemoSent(true);
      toast.success(t("demo.done"));
    } catch {
      toast.error("Ralat / Error");
    }
  };

  const oneCards = [
    { icon: Users, t: t("one.1.t"), d: t("one.1.d") },
    { icon: Wallet, t: t("one.2.t"), d: t("one.2.d") },
    { icon: FileCheck2, t: t("one.3.t"), d: t("one.3.d") },
    { icon: ScrollText, t: t("one.4.t"), d: t("one.4.d") },
    { icon: LayoutDashboard, t: t("one.5.t"), d: t("one.5.d") },
    { icon: Bot, t: t("one.6.t"), d: t("one.6.d") },
  ];

  const modules = [
    { icon: Users, t: t("mod.core"), d: t("mod.core.d") },
    { icon: Wallet, t: t("mod.fin"), d: t("mod.fin.d") },
    { icon: HandCoins, t: t("mod.lend"), d: t("mod.lend.d") },
    { icon: Vote, t: t("mod.gov"), d: t("mod.gov.d") },
    { icon: Bot, t: t("mod.ai"), d: t("mod.ai.d") },
    { icon: Building2, t: t("mod.plat"), d: t("mod.plat.d") },
  ];

  return (
    <div className="bg-background" data-testid="landing-page">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-brand-deep/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <LogoMark className="w-9 h-9" />
            <span className="font-display font-extrabold text-brand-dark text-lg tracking-tight">KOPERA</span>
          </Link>
          <nav className="hidden lg:flex items-center gap-7 ml-10 text-sm font-medium text-brand-dark/80">
            <a href="#modules" className="hover:text-brand-deep transition-colors">{t("nav.modules")}</a>
            <a href="#ai" className="hover:text-brand-deep transition-colors">AI</a>
            <a href="#security" className="hover:text-brand-deep transition-colors">{t("nav.security")}</a>
            <a href="#pricing" className="hover:text-brand-deep transition-colors">{t("nav.pricing")}</a>
            <a href="#faq" className="hover:text-brand-deep transition-colors">{t("nav.faq")}</a>
          </nav>
          <div className="flex-1" />
          <LangToggle />
          <Link to="/login" data-testid="nav-login-btn" className="hidden sm:inline-flex text-sm font-semibold text-brand-deep hover:text-brand-dark transition-colors">
            {t("nav.login")}
          </Link>
          <a href="#demo" data-testid="book-demo-btn" className="hidden sm:inline-flex bg-gold text-brand-dark text-sm font-bold px-4 py-2 rounded-full hover:bg-gold-hover transition-colors">
            {t("nav.bookDemo")}
          </a>
          <button className="lg:hidden p-2 text-brand-deep" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-nav-toggle">
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {menuOpen && (
          <div className="lg:hidden border-t border-brand-deep/10 bg-white px-6 py-4 space-y-3 text-sm font-medium">
            {["#modules|nav.modules", "#ai|AI", "#security|nav.security", "#pricing|nav.pricing", "#faq|nav.faq", "#demo|nav.bookDemo"].map((item) => {
              const [href, key] = item.split("|");
              return (
                <a key={href} href={href} onClick={() => setMenuOpen(false)} className="block text-brand-dark">
                  {key.startsWith("nav.") ? t(key) : key}
                </a>
              );
            })}
            <Link to="/login" className="block text-brand-deep font-semibold">{t("nav.login")}</Link>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative bg-white grid-bg overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0} className="inline-flex items-center gap-2.5 bg-slate-100 border border-slate-200 text-slate-700 text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-[0.15em]">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-500 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-sky-600" />
              </span>
              {t("hero.badge")}
            </motion.div>
            <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1} className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] tracking-tight text-balance">
              {t("hero.title")}
            </motion.h1>
            <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2} className="mt-5 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
              {t("hero.sub")}
            </motion.p>
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-6 flex flex-wrap gap-2">
              {[t("hero.pill1"), t("hero.pill2"), t("hero.pill3")].map((p) => (
                <span key={p} className="text-xs font-semibold text-slate-600 bg-white border border-slate-200 shadow-sm rounded-full px-3.5 py-1.5">{p}</span>
              ))}
            </motion.div>
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4} className="mt-8 flex flex-wrap gap-3">
              <a href="#demo" data-testid="hero-book-demo-btn" className="inline-flex items-center gap-2 bg-sky-600 text-white font-semibold px-6 py-3.5 rounded-full hover:bg-sky-700 hover:-translate-y-0.5 transition-all duration-150 shadow-sm">
                {t("hero.cta1")} <ArrowRight className="w-4 h-4" />
              </a>
              <Link to="/login" data-testid="hero-trial-btn" className="inline-flex items-center gap-2 bg-white text-slate-700 border border-slate-300 font-semibold px-6 py-3.5 rounded-full hover:border-slate-400 hover:-translate-y-0.5 transition-all duration-150">
                {t("hero.cta2")}
              </Link>
            </motion.div>
            <motion.p variants={fadeUp} initial="hidden" animate="show" custom={5} className="mt-4 text-xs text-slate-400">{t("hero.note")}</motion.p>
          </div>
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="lg:col-span-5 relative">
            <div className="absolute -inset-4 bg-sky-50 rounded-3xl" />
            <img src={HERO_IMG} alt="Kuala Lumpur Financial District" className="relative rounded-2xl shadow-xl w-full h-72 lg:h-96 object-cover border border-slate-200" />
            <div className="absolute -bottom-5 -left-5 bg-white rounded-xl border border-slate-200 shadow-lg px-5 py-4 hidden sm:block">
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-bold">MRR Sasaran</div>
              <div className="font-display text-xl font-bold text-slate-900">RM100K<span className="text-sky-600">+</span></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PROBLEMS */}
      <Section>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Eyebrow>KOPERA</Eyebrow>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-dark">{t("prob.title")}</h2>
            <div className="mt-8 space-y-4">
              {[t("prob.1"), t("prob.2"), t("prob.3"), t("prob.4")].map((p, i) => (
                <motion.div key={p} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} className="flex items-start gap-3 bg-card rounded-xl border border-red-200/60 p-4">
                  <X className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-brand-dark">{p}</span>
                </motion.div>
              ))}
              <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={4} className="flex items-start gap-3 bg-brand-dark rounded-xl p-4">
                <Check className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                <span className="text-sm font-semibold text-white">{t("prob.outro")}</span>
              </motion.div>
            </div>
          </div>
          <img src={BOARD_IMG} alt="Cooperative board meeting" className="rounded-2xl shadow-xl w-full h-80 lg:h-[28rem] object-cover" />
        </div>
      </Section>

      {/* ONE PLATFORM BENTO */}
      <Section className="bg-brand-light/60">
        <Eyebrow>{lang === "bm" ? "Perbezaan Utama" : "Key Differentiators"}</Eyebrow>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-dark max-w-2xl">{t("one.title")}</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {oneCards.map((c, i) => (
            <motion.div key={c.t} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className={`rounded-2xl p-6 border transition-all hover:-translate-y-1 hover:shadow-lg ${i === 5 ? "bg-brand-dark border-brand-deep text-white" : "bg-card border-brand-deep/10"}`}>
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${i === 5 ? "bg-gold/20" : "bg-brand-light"}`}>
                <c.icon className={`w-5 h-5 ${i === 5 ? "text-gold" : "text-brand-deep"}`} />
              </div>
              <div className={`mt-4 text-xs font-semibold uppercase tracking-[0.18em] ${i === 5 ? "text-sky-400" : "text-sky-600"}`}>{c.t}</div>
              <p className={`mt-2 text-sm leading-relaxed ${i === 5 ? "text-emerald-100/80" : "text-muted-foreground"}`}>{c.d}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* MODULES */}
      <Section id="modules">
        <Eyebrow>{t("nav.modules")}</Eyebrow>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-dark">{t("mod.title")}</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {modules.map((m, i) => (
            <motion.div key={m.t} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} className="bg-card rounded-2xl border border-brand-deep/10 p-6 hover:border-gold/60 hover:shadow-md transition-all">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-brand-deep flex items-center justify-center">
                  <m.icon className="w-5 h-5 text-gold" />
                </div>
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-brand-dark">{m.t}</span>
              </div>
              <p className="mt-3 text-sm text-muted-foreground">{m.d}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* AI */}
      <Section id="ai" className="bg-slate-900">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Eyebrow dark>Koperasi AI</Eyebrow>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white">{t("ai.title")}</h2>
            <p className="mt-4 text-emerald-100/70 leading-relaxed">{t("ai.sub")}</p>
            <div className="mt-8 grid grid-cols-2 gap-3">
              {["Member Support", "Finance Analyst", "Audit Assistant", "Management AI"].map((f) => (
                <div key={f} className="flex items-center gap-2 text-sm text-emerald-50 bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5">
                  <Bot className="w-4 h-4 text-gold shrink-0" /> {f}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-brand-surface border border-brand-edge rounded-2xl p-6 shadow-2xl">
            <div className="flex items-center gap-2 text-gold text-xs font-bold uppercase tracking-[0.15em] mb-4">
              <MessagesSquare className="w-4 h-4" /> Live Preview
            </div>
            <div className="space-y-3">
              <div className="flex justify-end"><div className="bg-brand-deep text-white text-sm rounded-2xl rounded-br-sm px-4 py-2.5 max-w-[80%]">Berapa baki saham saya sekarang?</div></div>
              <div className="flex justify-start"><div className="bg-white/10 text-emerald-50 text-sm rounded-2xl rounded-bl-sm px-4 py-2.5 max-w-[85%]">Baki saham anda ialah <span className="font-mono font-semibold text-gold">RM 5,200.00</span>. Yuran bulan ini telah dijelaskan. Terima kasih!</div></div>
              <div className="flex justify-end"><div className="bg-brand-deep text-white text-sm rounded-2xl rounded-br-sm px-4 py-2.5 max-w-[80%]">Bila AGM tahun ini?</div></div>
              <div className="flex justify-start"><div className="bg-white/10 text-emerald-50 text-sm rounded-2xl rounded-bl-sm px-4 py-2.5 max-w-[85%] animate-pulse-soft">…</div></div>
            </div>
          </div>
        </div>
      </Section>

      {/* SECURITY */}
      <Section id="security">
        <div className="text-center max-w-2xl mx-auto">
          <Eyebrow>{t("nav.security")}</Eyebrow>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-dark">{t("sec.title")}</h2>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[ShieldCheck, Database, ScrollText, FileCheck2].map((Icon, i) => (
            <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} className="bg-card rounded-2xl border border-brand-deep/10 p-6 text-center hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-full bg-brand-light mx-auto flex items-center justify-center">
                <Icon className="w-6 h-6 text-brand-deep" />
              </div>
              <div className="mt-4 text-sm font-semibold text-brand-dark">{t(`sec.${i + 1}`)}</div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* PRICING */}
      <Section id="pricing" className="bg-brand-light/60">
        <div className="text-center max-w-2xl mx-auto">
          <Eyebrow>{t("nav.pricing")}</Eyebrow>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-dark">{t("price.title")}</h2>
          <div className="mt-6 inline-flex rounded-full border border-brand-deep/20 bg-white p-1">
            {[["monthly", t("price.monthly")], ["annual", t("price.annual")]].map(([c, label]) => (
              <button key={c} data-testid={`pricing-cycle-${c}`} onClick={() => setCycle(c)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${cycle === c ? "bg-brand-deep text-white" : "text-brand-deep"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {PLAN_CARDS.map((p, i) => (
            <motion.div key={p.code} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              data-testid={p.testid}
              className={`relative rounded-2xl p-6 flex flex-col ${p.featured ? "bg-brand-dark text-white border-2 border-gold shadow-xl lg:-translate-y-3" : "bg-card border border-brand-deep/10"}`}>
              {p.featured && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-brand-dark text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">{t("price.popular")}</span>}
              <div className={`text-xs font-bold uppercase tracking-[0.18em] ${p.featured ? "text-gold" : "text-brand-deep"}`}>{p.code}</div>
              <div className={`mt-3 font-display text-3xl font-extrabold ${p.featured ? "text-white" : "text-brand-dark"}`}>
                RM{(cycle === "monthly" ? p.monthly : p.monthly * 10).toLocaleString()}
                <span className={`text-sm font-medium ${p.featured ? "text-emerald-100/60" : "text-muted-foreground"}`}>{cycle === "monthly" ? t("price.perMonth") : "/thn"}</span>
              </div>
              <div className={`mt-1 text-xs ${p.featured ? "text-emerald-100/60" : "text-muted-foreground"}`}>{t(`plan.${p.code}.target`)}</div>
              <ul className="mt-5 space-y-2 flex-1">
                {PLAN_FEATURES[p.code].map((f) => (
                  <li key={f} className={`flex items-start gap-2 text-xs ${p.featured ? "text-emerald-50/90" : "text-muted-foreground"}`}>
                    <Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${p.featured ? "text-gold" : "text-brand-vivid"}`} /> {f}
                  </li>
                ))}
              </ul>
              <Link to="/login" data-testid={`plan-cta-${p.code}`}
                className={`mt-6 text-center rounded-xl py-2.5 text-sm font-bold transition-colors ${p.featured ? "bg-gold text-brand-dark hover:bg-gold-hover" : "bg-brand-deep text-white hover:bg-brand-dark"}`}>
                {t("price.choose")}
              </Link>
            </motion.div>
          ))}
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={3} data-testid="plan-card-enterprise"
            className="rounded-2xl p-6 bg-gradient-to-br from-brand-surface to-brand-dark border border-brand-edge text-white flex flex-col">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-gold">ENTERPRISE</div>
            <div className="mt-3 font-display text-3xl font-extrabold">{t("price.custom")}</div>
            <div className="mt-1 text-xs text-emerald-100/60">{t("plan.enterprise.target")}</div>
            <ul className="mt-5 space-y-2 flex-1">
              {["Persekitaran berdedikasi", "Custom domain & SSO", "API lanjutan", "SLA & sokongan berdedikasi", "AI tersuai"].map((f) => (
                <li key={f} className="flex items-start gap-2 text-xs text-emerald-50/90">
                  <Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gold" /> {f}
                </li>
              ))}
            </ul>
            <a href="#demo" data-testid="plan-cta-enterprise" className="mt-6 text-center rounded-xl py-2.5 text-sm font-bold border border-gold text-gold hover:bg-gold hover:text-brand-dark transition-colors">
              {t("price.contact")}
            </a>
          </motion.div>
        </div>
        <p className="text-center text-xs text-muted-foreground mt-8">{t("price.trial")}</p>
      </Section>

      {/* FAQ */}
      <Section id="faq">
        <div className="max-w-3xl mx-auto">
          <div className="text-center">
            <Eyebrow>FAQ</Eyebrow>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-dark">{t("faq.title")}</h2>
          </div>
          <div className="mt-10 space-y-3">
            {FAQS.map(([qBm, aBm, qEn, aEn], i) => (
              <details key={i} className="group bg-card rounded-xl border border-brand-deep/10 open:border-gold/50 transition-colors" data-testid={`faq-item-${i}`}>
                <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between text-sm font-semibold text-brand-dark">
                  {lang === "bm" ? qBm : qEn}
                  <ArrowRight className="w-4 h-4 text-gold rotate-90 group-open:-rotate-90 transition-transform shrink-0 ml-4" />
                </summary>
                <div className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed">{lang === "bm" ? aBm : aEn}</div>
              </details>
            ))}
          </div>
        </div>
      </Section>

      {/* BOOK DEMO */}
      <Section id="demo" className="bg-slate-900">
        <div className="max-w-xl mx-auto">
          <div className="text-center">
            <Eyebrow dark>Demo</Eyebrow>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">{t("demo.title")}</h2>
            <p className="mt-3 text-sm text-emerald-100/70">{t("demo.sub")}</p>
          </div>
          {demoSent ? (
            <div className="mt-8 bg-brand-surface border border-gold/40 rounded-2xl p-8 text-center" data-testid="demo-success">
              <Check className="w-10 h-10 text-gold mx-auto" />
              <p className="mt-4 text-emerald-50 font-medium">{t("demo.done")}</p>
            </div>
          ) : (
            <form onSubmit={submitDemo} className="mt-8 bg-brand-surface border border-brand-edge rounded-2xl p-6 sm:p-8 space-y-4" data-testid="demo-form">
              <input data-testid="demo-coop-input" required placeholder={t("demo.coop")} value={form.coop_name} onChange={(e) => setForm({ ...form, coop_name: e.target.value })}
                className="w-full rounded-lg bg-white/5 border border-white/15 px-4 py-2.5 text-sm text-white placeholder:text-emerald-100/40 focus:outline-none focus:ring-2 focus:ring-gold/40" />
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="demo-person-input" required placeholder={t("demo.person")} value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                  className="w-full rounded-lg bg-white/5 border border-white/15 px-4 py-2.5 text-sm text-white placeholder:text-emerald-100/40 focus:outline-none focus:ring-2 focus:ring-gold/40" />
                <input data-testid="demo-email-input" required type="email" placeholder={t("demo.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full rounded-lg bg-white/5 border border-white/15 px-4 py-2.5 text-sm text-white placeholder:text-emerald-100/40 focus:outline-none focus:ring-2 focus:ring-gold/40" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="demo-phone-input" placeholder={t("demo.phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full rounded-lg bg-white/5 border border-white/15 px-4 py-2.5 text-sm text-white placeholder:text-emerald-100/40 focus:outline-none focus:ring-2 focus:ring-gold/40" />
                <select data-testid="demo-size-select" value={form.member_size} onChange={(e) => setForm({ ...form, member_size: e.target.value })}
                  className="w-full rounded-lg bg-white/5 border border-white/15 px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-gold/40">
                  <option value="" className="text-black">{t("demo.size")}</option>
                  {["0-100", "100-500", "500-1,000", "1,001-5,000", "5,001-10,000", "10,000+"].map((s) => <option key={s} value={s} className="text-black">{s}</option>)}
                </select>
              </div>
              <textarea data-testid="demo-message-input" rows={3} placeholder={t("demo.msg")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full rounded-lg bg-white/5 border border-white/15 px-4 py-2.5 text-sm text-white placeholder:text-emerald-100/40 focus:outline-none focus:ring-2 focus:ring-gold/40" />
              <button data-testid="demo-submit-btn" type="submit" className="w-full bg-gold text-brand-dark rounded-xl py-3 font-bold hover:bg-gold-hover transition-colors">
                {t("demo.submit")}
              </button>
            </form>
          )}
        </div>
      </Section>

      <footer className="bg-brand-night text-emerald-100/60 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start justify-between gap-8">
            <div className="max-w-sm">
              <div className="flex items-center gap-2.5">
                <LogoMark className="w-8 h-8" />
                <span className="font-display font-extrabold text-white">KOPERA</span>
              </div>
              <p className="mt-4 text-xs leading-relaxed">{t("footer.tag")}</p>
            </div>
            <div className="text-xs space-y-2">
              <div className="text-white font-semibold mb-3 uppercase tracking-[0.15em]">Platform</div>
              <a href="#modules" className="block hover:text-gold transition-colors">{t("nav.modules")}</a>
              <a href="#pricing" className="block hover:text-gold transition-colors">{t("nav.pricing")}</a>
              <a href="#demo" className="block hover:text-gold transition-colors">{t("nav.bookDemo")}</a>
              <Link to="/login" className="block hover:text-gold transition-colors">{t("nav.login")}</Link>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-white/10 text-[11px] flex flex-wrap gap-4 justify-between">
            <span>© 2026 KOPERA — Software for Cooperatives</span>
            <span>Multi-Tenant SaaS • White Label • Made for Malaysian Cooperatives</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
